import {
  CheckCircle2,
  Copy,
  FileUp,
  Loader2,
  Sparkles,
  XCircle,
} from "lucide-react";
import { useRef, useState, type DragEvent } from "react";

import { cn } from "@/lib/utils";

import { formatEuro } from "../gulaConstants";
import type { AccExpense } from "./accounting";
import { readInvoice } from "./accountingApi";
import { spanishDate } from "./gestorPackage";
import {
  removeAccountingFile,
  uploadAccountingFile,
} from "./useAccountingData";

type ItemState =
  | { step: "uploading" | "reading" }
  | { step: "done"; expense: AccExpense; attached: boolean }
  | { step: "duplicate"; text: string }
  | { step: "error"; text: string };

type Item = { id: string; name: string; state: ItemState };

const ACCEPTED = /\.(pdf|jpe?g|png|webp)$/i;
/** Files read at the same time (each read takes a few seconds) */
const PARALLEL = 3;

const today = () => new Date().toLocaleDateString("en-CA");

/**
 * Drop zone and button: every invoice is uploaded, read by the AI and saved as
 * an expense "to review". Duplicates are not saved twice.
 */
export const InvoiceUploader = ({
  onChanged,
  onOpen,
}: {
  onChanged: () => void;
  onOpen: (expense: AccExpense) => void;
}) => {
  const input = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [dragging, setDragging] = useState(false);

  const setState = (id: string, state: ItemState) =>
    setItems((previous) =>
      previous.map((item) => (item.id === id ? { ...item, state } : item)),
    );

  const process = async (item: Item, file: File) => {
    let path: string | null = null;
    try {
      const uploaded = await uploadAccountingFile(file, "expenses", today());
      path = uploaded.path;
      setState(item.id, { step: "reading" });
      const result = await readInvoice(uploaded.path, uploaded.name);
      if (result.duplicate) {
        await removeAccountingFile(uploaded.path).catch(() => undefined);
        setState(item.id, {
          step: "duplicate",
          text: `Ya estaba: ${result.duplicate.supplier_name} ${result.duplicate.invoice_number ?? ""} (${spanishDate(result.duplicate.invoice_date)})`,
        });
        return;
      }
      const expense = result.attached ?? result.expense;
      if (!expense) throw new Error("Sin datos");
      setState(item.id, {
        step: "done",
        expense,
        attached: Boolean(result.attached),
      });
    } catch (error) {
      if (path) await removeAccountingFile(path).catch(() => undefined);
      setState(item.id, {
        step: "error",
        text: error instanceof Error ? error.message : "Error",
      });
    }
  };

  const addFiles = async (files: FileList | File[]) => {
    const list = [...files];
    const fresh = list.map((file) => ({
      id: crypto.randomUUID(),
      name: file.name,
      state: ACCEPTED.test(file.name)
        ? ({ step: "uploading" } as ItemState)
        : ({
            step: "error",
            text: "Formato no admitido: PDF, JPG, PNG o WEBP",
          } as ItemState),
    }));
    setItems((previous) => [...fresh, ...previous]);
    const queue = fresh
      .map((item, index) => ({ item, file: list[index] }))
      .filter(({ item }) => item.state.step === "uploading");
    const worker = async () => {
      for (let next = queue.shift(); next; next = queue.shift()) {
        await process(next.item, next.file);
        onChanged();
      }
    };
    await Promise.all(Array.from({ length: PARALLEL }, worker));
    if (input.current) input.current.value = "";
  };

  const drop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files.length) addFiles(event.dataTransfer.files);
  };

  const busy = items.some(
    (item) => item.state.step === "uploading" || item.state.step === "reading",
  );

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={input}
        type="file"
        multiple
        accept="application/pdf,image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(event) =>
          event.target.files?.length && addFiles(event.target.files)
        }
      />
      <button
        type="button"
        onClick={() => input.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={drop}
        className={cn(
          "flex flex-col items-center gap-1 rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors",
          dragging
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:bg-muted/40",
        )}
      >
        <span className="flex items-center gap-2 font-medium">
          <FileUp className="h-5 w-5" />
          Arrastra aquí las facturas o pulsa para subirlas
        </span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5" />
          PDF o fotos, varias a la vez. La IA lee proveedor, NIF, fecha e
          importes y las deja «por revisar».
        </span>
      </button>

      {items.length ? (
        <ul className="flex flex-col divide-y rounded-lg border text-sm">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-2 px-3 py-2">
              {item.state.step === "uploading" ||
              item.state.step === "reading" ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
              ) : item.state.step === "done" ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
              ) : item.state.step === "duplicate" ? (
                <Copy className="h-4 w-4 shrink-0 text-amber-600" />
              ) : (
                <XCircle className="h-4 w-4 shrink-0 text-destructive" />
              )}
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {item.state.step === "uploading" ? (
                  "Subiendo…"
                ) : item.state.step === "reading" ? (
                  "Leyendo…"
                ) : item.state.step === "done" ? (
                  <button
                    type="button"
                    onClick={() =>
                      item.state.step === "done" && onOpen(item.state.expense)
                    }
                    className="text-foreground underline-offset-2 hover:underline"
                  >
                    {item.state.attached ? "Añadida a " : ""}
                    {item.state.expense.supplier_name} ·{" "}
                    {formatEuro(Number(item.state.expense.total))} · Revisar
                  </button>
                ) : (
                  "text" in item.state && item.state.text
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {items.length && !busy ? (
        <button
          type="button"
          onClick={() => setItems([])}
          className="self-end text-xs text-muted-foreground hover:text-foreground"
        >
          Limpiar lista
        </button>
      ) : null}
    </div>
  );
};
